<template>
    <div
        class="layout__main layout__main--no-top-padding layout__main--sidebar"
    >
        <BaseSection class="container-narrow">
            <template #title>
                <h1>
                    <template v-if="links?.length">
                        {{ title }}
                    </template>
                    <template v-else>
                        <ContentPlaceholdersText :lines="1" />
                    </template>
                </h1>
            </template>
            <template #description>
                <template v-if="description && links?.length">
                    {{ description }}
                </template>
                <template v-else>
                    <ContentPlaceholdersText :lines="3" />
                </template>
            </template>
        </BaseSection>

        <main class="bleed">
            <BaseSection class="container-narrow">
                <nav class="base-directory">
                    <div
                        v-for="letter in sorted"
                        :key="letter.letter"
                        class="base-directory__item"
                    >
                        <h3 class="base-directory__index">
                            {{ letter.letter.toUpperCase() }}
                        </h3>
                        <BaseQuicklinkList
                            :links="letter.items.map(t => ({
                                ...t,
                                text: t.contextTitle ?? t.title,
                                uri: `/${t.uri}`
                            }))"
                        />
                    </div>
                </nav>
            </BaseSection>
        </main>
    </div>
</template>

<script setup lang="ts">
type Link = {
    title
    uri
    children: {
        title
        uri
    }[]
    parent: {
        title
        uri
    }[]
}
type Props = {
    title: string,
    description?: string,
    links: Link[]
}

const props = defineProps<Props>();

function getFirstLetter(title: string): string {
    return title.trim().charAt(0).toLocaleLowerCase(); // Locale-aware for different languages
}

const sorted = computed(() => {
    if (!props?.links?.length) {
        return [];
    }

    const uniqueFirstLetters = new Set(
        props.links.map(condition => getFirstLetter(condition.title))
    );

    const sortedLetters = Array.from(uniqueFirstLetters).sort((a, b) => a.localeCompare(b));

    const mapped = sortedLetters.map(letter => ({
        letter,
        items: props.links.filter(condition =>
            condition.title.toLowerCase().startsWith(letter)
        )
    }));

    return mapped.filter(({ items }) => items.length);
});
</script>

<style lang="less">
.base-directory {
    display: flex;
    flex-direction: column;
    gap: 2.5rem;
}

.base-directory__item {
    display: flex;
    flex-direction: column;
    gap: 1.875rem;

    .quicklinks-list a:first-of-type span {
        margin-top: -1rem;
    }
}

.base-directory__index {
    .typography-h4();
}
</style>
